import { useEffect, useMemo, useRef, useState } from 'react';
import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import { useProjectId } from '@client/store/project/project.hook';
import { hydrateBlueprintRaster } from '../helpers/blueprint-underlay-persist.helpers';
import { resolveBlueprintRaster } from '../helpers/blueprint-underlay-raster.cache';

/**
 * Attach cached/fetched JPEG data URLs so the map and open page details can
 * render without storing rasters on the project document.
 *
 * Only {@link hydrateIds} are fetched. Other pages keep a cached raster when
 * one already exists, and otherwise stay without `imageDataUrl` until the
 * caller asks for them.
 */
export const useBlueprintUnderlaysWithRasters = (
  underlays: readonly BlueprintUnderlay[],
  hydrateIds: readonly string[],
): BlueprintUnderlay[] => {
  const projectId = useProjectId();
  const [epoch, setEpoch] = useState(0);
  const wantedIds = useMemo(() => new Set(hydrateIds), [hydrateIds]);
  const hydrateKey = underlays
    .filter((underlay) => wantedIds.has(underlay.id))
    .map((underlay) => `${underlay.id}:${underlay.imageRef ?? ''}`)
    .join(',');
  const underlaysRef = useRef(underlays);
  underlaysRef.current = underlays;
  const hydrateIdsRef = useRef(hydrateIds);
  hydrateIdsRef.current = hydrateIds;

  useEffect(() => {
    if (!hydrateKey) return;
    let cancelled = false;
    const wanted = new Set(hydrateIdsRef.current);
    // Fetch only the pages the current view paints. A project with many PDF
    // pages must not GET every raster on navigation.
    const pages = underlaysRef.current.filter((underlay) =>
      wanted.has(underlay.id),
    );
    void Promise.all(
      pages.map((underlay) => hydrateBlueprintRaster(projectId, underlay)),
    ).then(() => {
      if (!cancelled) setEpoch((value) => value + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [hydrateKey, projectId]);

  return useMemo(() => {
    void epoch;
    return underlays.map((underlay) => {
      const imageDataUrl = resolveBlueprintRaster(
        underlay.id,
        underlay.imageDataUrl,
      );
      if (!imageDataUrl || imageDataUrl === underlay.imageDataUrl) {
        return underlay;
      }
      return { ...underlay, imageDataUrl };
    });
  }, [epoch, underlays]);
};
